// FEAT-38 — browser-automation traits, probed ONCE at the gate and sent on
// `attempt_started` beside the extension traces (see ./extensions.ts for the
// same honesty posture).
//
// THE LIMIT, stated plainly. `navigator.webdriver === true` is what a plain
// automation library (Playwright, Puppeteer, Selenium) reports, and ONE
// stealth plugin turns it off (measured in
// docs/product/ai-agent-cheating-research.md §4). The headless traits (no
// plugins + a "HeadlessChrome" brand) describe the old headless mode only.
// So a `true` is a fair thing to record — a human browser never reports
// either — and a `false` proves nothing. CONTEXT for the recruiter's attempt
// page: backend/risk.py never reads it and nothing here blocks or flags.
// `signed_agent` (the Web Bot Auth `Signature-Agent` header) is the third
// trait, stamped by the SERVER at ingest — the browser never sets it.
//
// Pure over an environment record, so it unit-tests without jsdom; the DOM
// wrapper fails safe to all-false.

export interface AutomationTraits {
  /** `navigator.webdriver === true`. */
  webdriver: boolean;
  /** Zero plugins AND a "HeadlessChrome" brand or user-agent string. */
  headless: boolean;
}

export interface AutomationEnv {
  webdriver: unknown;
  pluginCount: unknown;
  brands: readonly unknown[];
  userAgent: unknown;
}

const HEADLESS_NAME_RE = /headlesschrome/i;

const ALL_FALSE: Readonly<AutomationTraits> = Object.freeze({
  webdriver: false,
  headless: false,
});

/** PURE. Only an exact `true` counts as webdriver (Firefox's
 *  `resistFingerprinting` reports `undefined`, which must read as "no"); the
 *  headless trait needs BOTH an empty plugin list and the headless name, so a
 *  plugin-less real browser never trips it. */
export function detectAutomationTraits(env: AutomationEnv): AutomationTraits {
  const webdriver = env.webdriver === true;
  const named = (v: unknown): boolean => typeof v === "string" && HEADLESS_NAME_RE.test(v);
  const headlessName =
    (Array.isArray(env.brands) && env.brands.some(named)) || named(env.userAgent);
  const plugins =
    typeof env.pluginCount === "number" && Number.isFinite(env.pluginCount)
      ? env.pluginCount
      : Number.NaN;
  return { webdriver, headless: plugins === 0 && headlessName };
}

/** DOM wrapper. Never throws: a navigator that refuses any of the reads (a
 *  locked-down profile, an old engine) reads as a plain browser. */
export function probeAutomationTraits(nav?: Navigator): AutomationTraits {
  try {
    const n = nav ?? (typeof navigator !== "undefined" ? navigator : undefined);
    if (!n) return { ...ALL_FALSE };
    const uaData = (n as Navigator & { userAgentData?: { brands?: unknown } }).userAgentData;
    const rawBrands = uaData?.brands;
    const brands = Array.isArray(rawBrands)
      ? rawBrands.map((b) => (b && typeof b === "object" ? (b as { brand?: unknown }).brand : b))
      : [];
    return detectAutomationTraits({
      webdriver: n.webdriver,
      pluginCount: n.plugins?.length,
      brands,
      userAgent: n.userAgent,
    });
  } catch {
    return { ...ALL_FALSE };
  }
}
